// 描画エンジンのシングルトン。CanvasView がキャンバスを渡して起動し、
// documentStore の変更と uiStore のビュー変更で再描画を予約する。

import { watch } from 'vue'
import { createGLContext, type GLContextHandle } from '@/gl/context'
import { Compositor } from '@/render/compositor'
import { BrushEngine, type StrokeParams } from '@/brush/brushEngine'
import { FilterEngine } from '@/filters/gpu'
import { getFilter, normalizeParams, type FilterParams, type FilterDef, type FilterPass } from '@/filters/registry'
import { runCpuFilterAsync } from '@/workers/clients'
import { createTexture, deleteTexture } from '@/gl/texture'
import { maskToAlpha } from '@/brush/fill'
import type { StrokeSample } from '@/brush/dabs'
import { rectIntersect, type Rect } from '@/types/geometry'
import { ViewRenderer } from '@/render/viewRenderer'
import { renderDocumentToPixels, type ExportedPixels } from '@/render/exportRenderer'
import { useDocumentStore } from '@/stores/documentStore'
import { useUiStore } from '@/stores/uiStore'
import { theme } from '@/composables/useTheme'
import { pixelStore } from '@/pixels/pixelStore'
import type { RasterLayer, Document } from '@/types/document'
import { flattenLayerIds, findParentId } from '@/model/layerTree'
import { styleExtent } from '@/render/styles/effectOrder'
import { toast } from '@/composables/useToast'
import { t } from '@/i18n'
import { onFontsReady } from '@/text/fonts'
import { dropTextRasters } from '@/pixels/rasterCache'

interface Engine {
  handle: GLContextHandle
  compositor: Compositor
  view: ViewRenderer
  brush: BrushEngine
  filter: FilterEngine
}

let engine: Engine | null = null
let frameRequested = false
let compositeDirty = true
/** 次の合成で描き直す範囲。null = 全体 */
let pendingDirty: Rect | null = null
let stopWatchers: (() => void)[] = []
/** 計測用 */
export const frameStats = { frames: 0, composites: 0, partialComposites: 0, lastCompositeMs: 0, lastFrameMs: 0 }

function cssColor(name: string): [number, number, number] {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  const n = parseInt(v.replace('#', ''), 16)
  if (Number.isNaN(n)) return [0.5, 0.5, 0.5]
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

// 同梱フォントは unicode-range で分割されていて、初回は代替書体で描かれる。
// 読み込みが終わったらテキストのラスタを捨てて描き直す
onFontsReady(() => {
  dropTextRasters()
  requestFrame(true)
})

export function requestFrame(recomposite = false, rect?: Rect | null): void {
  if (recomposite) {
    if (compositeDirty) {
      // 既に全体が予約されていれば矩形は無視。矩形同士は和集合
      if (pendingDirty && rect) pendingDirty = unionRect(pendingDirty, rect)
      else if (!rect) pendingDirty = null
    } else {
      pendingDirty = rect ?? null
    }
    compositeDirty = true
  }
  if (frameRequested) return
  frameRequested = true
  requestAnimationFrame(() => {
    frameRequested = false
    renderNow()
  })
}

function renderNow(): void {
  if (!engine || engine.handle.lost) return
  const docStore = useDocumentStore()
  const ui = useUiStore()
  const { gl, canvas } = engine.handle
  const dpr = window.devicePixelRatio || 1
  const cssW = ui.viewportSize.w
  const cssH = ui.viewportSize.h
  const W = Math.max(1, Math.round(cssW * dpr))
  const H = Math.max(1, Math.round(cssH * dpr))
  if (canvas.width !== W || canvas.height !== H) {
    canvas.width = W
    canvas.height = H
  }
  const doc = docStore.doc
  if (!doc) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    gl.viewport(0, 0, W, H)
    const [r, g, b] = cssColor('--canvas-bg')
    gl.clearColor(r, g, b, 1)
    gl.clear(gl.COLOR_BUFFER_BIT)
    return
  }
  try {
    const t0 = performance.now()
    let composite = engine.compositor.cached
    if (compositeDirty || !composite) {
      let dirty = composite ? pendingDirty : null
      if (dirty) {
        // レイヤースタイルは本体の外側へはみ出すので、その最大分だけ広げる
        let ext = 0
        for (const l of Object.values(doc.layers)) ext = Math.max(ext, styleExtent(l.styles))
        if (ext > 0) dirty = { x: dirty.x - ext, y: dirty.y - ext, w: dirty.w + ext * 2, h: dirty.h + ext * 2 }
      }
      composite = engine.compositor.render(doc, dirty)
      frameStats.composites++
      if (engine.compositor.lastStats.partial) frameStats.partialComposites++
      frameStats.lastCompositeMs = performance.now() - t0
      compositeDirty = false
      pendingDirty = null
    }
    frameStats.frames++
    let selection: { texture: import('@/gl/texture').Texture; rect: import('@/types/geometry').Rect } | null = null
    const selId = doc.selection.pixelId
    const selSurface = selId ? pixelStore.get(selId) : undefined
    if (selSurface) {
      const t = engine.compositor.layerTextures.get(selSurface)
      if (t) selection = { texture: t.texture, rect: t.rect }
    }
    engine.view.draw(composite, ui.view, dpr, cssW, cssH, {
      checkerA: cssColor('--checker-a'),
      checkerB: cssColor('--checker-b'),
      background: cssColor('--canvas-bg')
    }, selection, antsPhase, ui.quickMask ? { live: quickMaskLive } : null)
    scheduleAnts(!!selection)
    frameStats.lastFrameMs = performance.now() - t0
  } catch (e) {
    console.error(e)
    toast.error(e instanceof Error ? e.message : String(e))
  }
}

// マーチングアンツのアニメーション (選択範囲があるときだけ 10fps で再描画)
let antsPhase = 0
let antsTimer: number | null = null
function scheduleAnts(active: boolean) {
  if (active && antsTimer === null) {
    antsTimer = window.setInterval(() => {
      antsPhase = (antsPhase + 1) % 8
      requestFrame(false)
    }, 100)
  } else if (!active && antsTimer !== null) {
    clearInterval(antsTimer)
    antsTimer = null
  }
}

/** 合成結果 (文書全体) を読み出す。自動選択の「全レイヤーを対象」用 */
export function readComposite(): ExportedPixels {
  return exportPixels()
}

/**
 * 統計用: 直前に表示した合成結果を再合成せずに読む (ヒストグラム等)。
 * 未描画なら通常の合成を行う。
 */
export function readCompositeCached(): ExportedPixels | null {
  if (!engine) return null
  const doc = useDocumentStore().doc
  if (!doc) return null
  const cached = engine.compositor.cached
  if (!cached || cached.width !== doc.meta.width || cached.height !== doc.meta.height) return exportPixels()
  return { width: cached.width, height: cached.height, rgba: engine.compositor.readPixels({ x: 0, y: 0, w: cached.width, h: cached.height }) }
}

export function startEngine(canvas: HTMLCanvasElement): boolean {
  stopEngine()
  const handle = createGLContext(canvas)
  if (!handle) return false
  const build = () => {
    const compositor = new Compositor(handle.gl, handle.maxTextureSize)
    const view = new ViewRenderer(handle.gl)
    const brush = new BrushEngine(handle.gl)
    const filter = new FilterEngine(handle.gl)
    engine = { handle, compositor, view, brush, filter }
  }
  build()
  handle.onLost(() => {
    toast.warn(t('toast.webglLost'))
    engine?.compositor.dispose()
    engine?.view.dispose()
    engine?.brush.dispose()
    engine?.filter.dispose()
  })
  handle.onRestored(() => {
    build()
    toast.success(t('toast.webglRestored'))
    requestFrame(true)
  })

  const docStore = useDocumentStore()
  const ui = useUiStore()
  stopWatchers.push(docStore.onChange(info => {
    if (info.kind === 'view-only') requestFrame(false)
    else if (info.kind === 'rect') requestFrame(true, info.rect)
    else requestFrame(true, null)
  }))
  stopWatchers.push(watch(() => [ui.view, ui.viewportSize], () => requestFrame(false), { deep: true }))
  stopWatchers.push(watch(theme, () => requestFrame(false)))
  stopWatchers.push(watch(() => ui.quickMask, () => requestFrame(false)))
  requestFrame(true)
  return true
}

export function stopEngine(): void {
  for (const s of stopWatchers) s()
  stopWatchers = []
  scheduleAnts(false)
  if (engine) {
    engine.compositor.dispose()
    engine.view.dispose()
    engine.brush.dispose()
    engine.filter.dispose()
    engine.handle.dispose()
    engine = null
  }
}

/** 書き出し: 表示と同じコンポジタで全解像度を合成して読み出す */
export function exportPixels(): ExportedPixels {
  if (!engine) throw new Error('エンジンが起動していない')
  const doc = useDocumentStore().doc
  if (!doc) throw new Error('ドキュメントがない')
  const px = renderDocumentToPixels(engine.compositor, doc)
  compositeDirty = true
  requestFrame(true)
  return px
}

export function engineStats(): { gpuBytes: number } {
  return { gpuBytes: engine?.compositor.layerTextures.byteLength() ?? 0 }
}

export function isCompositeDirty(): boolean {
  return compositeDirty
}

export function compositorStats() {
  return engine ? { ...engine.compositor.lastStats, textures: { ...engine.compositor.layerTextures.stats }, gpuBytes: engine.compositor.layerTextures.byteLength() } : null
}

// ---------- クイックマスク表示 ----------
let quickMaskLive: { coverage: import('@/gl/texture').Texture; subtract: boolean } | null = null
export function quickMaskLiveState() {
  return quickMaskLive
}

// ---------- ブラシストローク ----------

function selectionTexture(): { texture: import('@/gl/texture').Texture; rect: Rect } | null {
  if (!engine) return null
  const doc = useDocumentStore().doc
  const selId = doc?.selection.pixelId
  const s = selId ? pixelStore.get(selId) : undefined
  if (!s) return null
  const t = engine.compositor.layerTextures.get(s)
  return t ? { texture: t.texture, rect: t.rect } : null
}

export function beginStroke(params: StrokeParams, quickMask = false): boolean {
  if (!engine || engine.handle.lost) return false
  const doc = useDocumentStore().doc
  if (!doc) return false
  engine.brush.begin(params, doc.meta.width, doc.meta.height)
  if (quickMask) {
    // 選択範囲を塗る: レイヤーには乗せず、カバレッジをクイックマスク表示に使う
    engine.compositor.liveStroke = null
    quickMaskLive = { coverage: engine.brush.coverageTexture!, subtract: params.mode === 'erase' }
    return true
  }
  quickMaskLive = null
  const modes = { paint: 0, erase: 1, clone: 2, dodge: 3, burn: 4, sponge: 5, desponge: 6, blur: 7, sharpen: 8 }
  engine.compositor.liveStroke = {
    layerId: params.layerId,
    coverage: engine.brush.coverageTexture!,
    color: params.color,
    opacity: params.opacity,
    mode: modes[params.mode],
    cloneOffset: params.cloneOffset ?? { x: 0, y: 0 },
    strength: params.strength ?? 0.5,
    lockTransparency: params.lockTransparency,
    selection: selectionTexture()
  }
  return true
}

export function addStrokeSample(s: StrokeSample): void {
  if (!engine || !engine.brush.active) return
  const r = engine.brush.addSample(s)
  if (r) requestFrame(true, r)
}

/** ストロークを確定してレイヤーへ書き戻す (履歴 1 エントリ) */
export function endStroke(last: StrokeSample, label = 'history.brush'): void {
  if (!engine || !engine.brush.active) return
  engine.brush.finish(last)
  const docStore = useDocumentStore()
  const doc = docStore.doc
  const live = engine.compositor.liveStroke
  const bounds = engine.brush.strokeBounds
  const params = engine.brush.params
  engine.brush.end()
  if (!doc || !live || !bounds || !params) {
    engine.compositor.liveStroke = null
    requestFrame(true)
    return
  }
  const rect = rectIntersect(bounds, { x: 0, y: 0, w: doc.meta.width, h: doc.meta.height })
  if (!rect) {
    engine.compositor.liveStroke = null
    requestFrame(true)
    return
  }
  const rgba = engine.compositor.bakeLiveStroke(doc, rect)
  engine.compositor.liveStroke = null
  if (rgba) {
    const layer = docStore.rasterLayer(params.layerId)
    if (layer) {
      const local = { x: rect.x - layer.offset.x, y: rect.y - layer.offset.y, w: rect.w, h: rect.h }
      docStore.mutate(label, (draft, px) => {
        px.writeRect(layer.pixelId, local, rgba)
        void draft
        return { kind: 'rect', rect }
      })
      return
    }
  }
  requestFrame(true, rect)
}

/**
 * 修復ブラシ / スポット修復用: ストロークを確定せず、範囲・元画素・(クローン後の) 画素・カバレッジを返す。
 * 呼び出し側が Worker で合成してから書き戻す。
 */
export function finishStrokeForHeal(last: StrokeSample): { layerId: string; rect: Rect; local: Rect; original: Uint8ClampedArray; cloned: Uint8ClampedArray; coverage: Uint8ClampedArray } | null {
  if (!engine || !engine.brush.active) return null
  engine.brush.finish(last)
  const docStore = useDocumentStore()
  const doc = docStore.doc
  const live = engine.compositor.liveStroke
  const bounds = engine.brush.strokeBounds
  const params = engine.brush.params
  const layer = params ? docStore.rasterLayer(params.layerId) : undefined
  // 周囲からパッチを探すので、ストローク範囲の 1.5 倍以上 (最低 24px) 広げる
  const pad = bounds ? Math.max(24, Math.round(Math.max(bounds.w, bounds.h) * 1.5)) : 24
  const rect = doc && bounds ? rectIntersect({ x: bounds.x - pad, y: bounds.y - pad, w: bounds.w + pad * 2, h: bounds.h + pad * 2 }, { x: 0, y: 0, w: doc.meta.width, h: doc.meta.height }) : null
  let result: ReturnType<typeof finishStrokeForHeal> = null
  if (doc && live && rect && params && layer) {
    const coverage = engine.brush.readCoverage(rect)
    const cloned = engine.compositor.bakeLiveStroke(doc, rect)
    const local = { x: rect.x - layer.offset.x, y: rect.y - layer.offset.y, w: rect.w, h: rect.h }
    const original = pixelStore.get(layer.pixelId)?.readRect(local)
    if (cloned && original) result = { layerId: layer.id, rect, local, original, cloned, coverage }
  }
  engine.brush.end()
  engine.compositor.liveStroke = null
  requestFrame(true, rect)
  return result
}

/**
 * レイヤー群を 1 枚のラスターに結合する (透明の上で互いのブレンド・スタイルを適用した結果)。
 * ids は文書内の順序で並べ直す。
 */
export function mergeLayers(ids: string[], name?: string, keepHidden = true): string | null {
  if (!engine) return null
  const docStore = useDocumentStore()
  const doc = docStore.doc
  if (!doc || !ids.length) return null
  const order = flattenLayerIds(doc)
  const sorted = [...ids].filter(id => doc.layers[id]).sort((a, b) => order.indexOf(a) - order.indexOf(b))
  if (!sorted.length) return null
  const temp: Document = { ...doc, meta: { ...doc.meta, background: 'transparent' }, rootChildren: sorted, layers: { ...doc.layers } }
  for (const id of sorted) {
    const l = temp.layers[id]!
    // 非表示のレイヤーを結合対象に含めた場合は表示扱いにしない (Photoshop と同じ: 非表示は捨てる)
    if (!l.visible) temp.rootChildren = temp.rootChildren.filter(x => x !== id)
  }
  const px = renderDocumentToPixels(engine.compositor, temp)
  compositeDirty = true
  const top = sorted[sorted.length - 1]!
  const id = docStore.replaceLayersWithRaster(sorted, px.rgba, name ?? doc.layers[top]!.name, 'history.merge', keepHidden)
  requestFrame(true)
  return id
}
/** レイヤー 1 枚 (グループなら丸ごと) を透明の上で描いた文書サイズの RGBA */
export function renderLayerAlone(id: string): ExportedPixels | null {
  if (!engine) return null
  const doc = useDocumentStore().doc
  if (!doc || !doc.layers[id]) return null
  const temp: Document = { ...doc, meta: { ...doc.meta, background: 'transparent' }, rootChildren: [id], layers: { ...doc.layers, [id]: { ...doc.layers[id]!, visible: true } as Document['layers'][string] } }
  const px = renderDocumentToPixels(engine.compositor, temp)
  compositeDirty = true
  requestFrame(true)
  return px
}
/** アクティブレイヤーを直下のレイヤーと結合 */
export function mergeDown(): string | null {
  const docStore = useDocumentStore()
  const doc = docStore.doc
  const id = doc?.activeLayerIds[0]
  if (!doc || !id) return null
  const parent = findParentId(doc, id)
  const siblings = parent ? (doc.layers[parent] as { children: string[] }).children : doc.rootChildren
  const i = siblings.indexOf(id)
  if (i <= 0) return null
  const below = siblings[i - 1]!
  const bl = doc.layers[below]
  if (!bl || bl.kind === 'adjustment' || bl.kind === 'fill') return null
  return mergeLayers([below, id], bl.name)
}
/** 表示中のルートレイヤーをすべて結合 */
export function mergeVisible(): string | null {
  const doc = useDocumentStore().doc
  if (!doc) return null
  const ids = doc.rootChildren.filter(id => doc.layers[id]?.visible)
  if (!ids.length) return null
  return mergeLayers(ids, doc.layers[ids[ids.length - 1]!]!.name)
}
/** 画像を統合: 表示レイヤーを結合し、非表示は捨てて「背景」にする */
export function flattenImage(): string | null {
  const doc = useDocumentStore().doc
  if (!doc) return null
  const ids = doc.rootChildren.filter(id => doc.layers[id]?.visible)
  if (!ids.length) return null
  return mergeLayers(ids, '背景', false)
}

/** クイックマスク用: ストロークをレイヤーではなく選択範囲へ書く */
export function endQuickMaskStroke(last: StrokeSample, subtract: boolean, opacity: number): void {
  if (!engine || !engine.brush.active) return
  engine.brush.finish(last)
  const docStore = useDocumentStore()
  const doc = docStore.doc
  const bounds = engine.brush.strokeBounds
  const rect = doc && bounds ? rectIntersect(bounds, { x: 0, y: 0, w: doc.meta.width, h: doc.meta.height }) : null
  const cov = rect ? engine.brush.readCoverage(rect) : null
  engine.brush.end()
  engine.compositor.liveStroke = null
  if (rect && cov) docStore.paintSelection(rect, cov, subtract, opacity)
  requestFrame(true, rect)
}

export function cancelStroke(): void {
  if (!engine) return
  const b = engine.brush.strokeBounds
  engine.brush.end()
  engine.compositor.liveStroke = null
  requestFrame(true, b)
}

function unionRect(a: Rect, b: Rect): Rect {
  const x0 = Math.min(a.x, b.x), y0 = Math.min(a.y, b.y)
  const x1 = Math.max(a.x + a.w, b.x + b.w), y1 = Math.max(a.y + a.h, b.y + b.h)
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }
}

/** 合成結果の 1 ピクセルを読む (スポイト)。文書外なら null */
export function sampleComposite(x: number, y: number): [number, number, number, number] | null {
  if (!engine) return null
  const doc = useDocumentStore().doc
  if (!doc) return null
  const ix = Math.floor(x), iy = Math.floor(y)
  if (ix < 0 || iy < 0 || ix >= doc.meta.width || iy >= doc.meta.height) return null
  // 直前の合成結果から読む (再合成しない)。無ければ 1 回だけ合成する
  const cached = engine.compositor.cached
  if (!cached || cached.width !== doc.meta.width || cached.height !== doc.meta.height) {
    engine.compositor.render(doc)
    requestFrame(true)
  }
  const px = engine.compositor.readPixels({ x: ix, y: iy, w: 1, h: 1 })
  return [px[0]!, px[1]!, px[2]!, px[3]!]
}

/** 自由変形のプレビュー行列を設定 / 解除する */
export function setLiveTransform(layerId: string | null, matrix?: import('@/types/geometry').Mat3): void {
  if (!engine) return
  engine.compositor.liveTransform = layerId && matrix ? { layerId, matrix } : null
  requestFrame(true, null)
}

/** テスト用: WEBGL_lose_context でコンテキストを失わせ、指定 ms 後に復旧させる */
export function loseContextForTest(restoreAfterMs = 100): boolean {
  if (!engine) return false
  const ext = engine.handle.gl.getExtension('WEBGL_lose_context')
  if (!ext) return false
  ext.loseContext()
  setTimeout(() => ext.restoreContext(), restoreAfterMs)
  return true
}

// ---------- フィルター ----------

interface FilterRun {
  /** レイヤー座標での処理矩形 (extent 分の余白込み) */
  local: Rect
  /** 文書座標 */
  docRect: Rect
  fb: import('@/gl/texture').Framebuffer
  inputTex: import('@/gl/texture').Texture
  /** 作成したエンジン (await の間に再起動されていたら使わない) */
  owner: Engine
}

/** 対象レイヤーにフィルターを掛けた結果 (FBO) を作る。呼び出し側が releaseFilterRun する */
interface FilterInput {
  layer: RasterLayer
  def: FilterDef
  p: FilterParams
  local: Rect
  docRect: Rect
  data: Uint8ClampedArray
}

function prepareFilterInput(layerId: string, filterId: string, params: FilterParams): FilterInput | null {
  if (!engine) return null
  const docStore = useDocumentStore()
  const doc = docStore.doc
  const layer = docStore.rasterLayer(layerId)
  const def = getFilter(filterId)
  if (!doc || !layer || !def) return null
  const surface = pixelStore.get(layer.pixelId)
  const b = surface?.contentBounds()
  if (!surface || !b) return null
  const p = normalizeParams(def, params)
  const ext = def.extent(p)
  // 処理範囲: 内容矩形 ∩ (文書 ∪ 余白) に余白を足す
  const local: Rect = { x: b.x - ext, y: b.y - ext, w: b.w + ext * 2, h: b.h + ext * 2 }
  if (local.w > engine.handle.maxTextureSize || local.h > engine.handle.maxTextureSize) return null
  const data = surface.readRect(local)
  return { layer, def, p, local, docRect: { x: local.x + layer.offset.x, y: local.y + layer.offset.y, w: local.w, h: local.h }, data }
}

function finishFilterRun(input: FilterInput, data: Uint8ClampedArray, passes: FilterPass[]): FilterRun | null {
  if (!engine) return null
  const { local } = input
  const inputTex = createTexture(engine.handle.gl, local.w, local.h, 4, data)
  const fb = engine.filter.run(inputTex, local.w, local.h, passes, { x: local.w / 2, y: local.h / 2 })
  return { local, docRect: input.docRect, fb, inputTex, owner: engine }
}

/**
 * GPU フィルターは同期に結果を返す (await を挟むと Vue の再描画でエンジンが入れ替わることがあるため)。
 * CPU フィルターは Worker で非同期に実行し、待っている間にエンジンが再起動されたら null。
 */
function runFilterOn(layerId: string, filterId: string, params: FilterParams): FilterRun | null | Promise<FilterRun | null> {
  const input = prepareFilterInput(layerId, filterId, params)
  if (!input) return null
  if (input.def.cpu) {
    const eng = engine
    return runCpuFilterAsync(input.def.id, input.data, input.local.w, input.local.h, input.p).then(out => (engine && engine === eng ? finishFilterRun(input, out, []) : null))
  }
  return finishFilterRun(input, input.data, input.def.passes(input.p))
}

function releaseFilterRun(r: FilterRun) {
  // 別のエンジンで作った FBO はコンテキストごと消えているので触らない
  if (!engine || engine !== r.owner) return
  engine.filter.release(r.fb)
  deleteTexture(engine.handle.gl, r.inputTex)
}

let livePreview: FilterRun | null = null
let previewSeq = 0
let liveImage: FilterRun | null = null

/** 任意の RGBA (文書座標 docRect) をレイヤーの表示に差し替える (ワープなどの CPU プレビュー) */
export function setLiveImagePreview(layerId: string, rgba: Uint8ClampedArray, docRect: Rect): void {
  if (!engine) return
  clearLiveImagePreview(false)
  const gl = engine.handle.gl
  if (docRect.w <= 0 || docRect.h <= 0 || docRect.w > engine.handle.maxTextureSize || docRect.h > engine.handle.maxTextureSize) return
  const inputTex = createTexture(gl, docRect.w, docRect.h, 4, rgba)
  const fb = engine.filter.run(inputTex, docRect.w, docRect.h, [], { x: 0, y: 0 })
  liveImage = { local: { x: 0, y: 0, w: docRect.w, h: docRect.h }, docRect, fb, inputTex, owner: engine }
  engine.compositor.liveFilter = { layerId, texture: fb.texture, rect: docRect }
  requestFrame(true)
}
export function clearLiveImagePreview(redraw = true): void {
  if (!engine) return
  const r = liveImage
  liveImage = null
  if (r) {
    if (engine.compositor.liveFilter?.texture === r.fb.texture) engine.compositor.liveFilter = null
    releaseFilterRun(r)
    if (redraw) requestFrame(true)
  }
}

/** プレビュー: 対象レイヤーの表示をフィルター結果に差し替える。古い要求の結果は捨てる */
export async function previewFilter(layerId: string, filterId: string, params: FilterParams): Promise<boolean> {
  if (!engine) return false
  const my = ++previewSeq
  const pending = runFilterOn(layerId, filterId, params)
  const r = pending instanceof Promise ? await pending : pending
  if (!engine || my !== previewSeq) {
    if (r) releaseFilterRun(r)
    return false
  }
  clearFilterPreview()
  if (!r) return false
  livePreview = r
  engine.compositor.liveFilter = { layerId, texture: r.fb.texture, rect: r.docRect }
  requestFrame(true, r.docRect)
  return true
}

export function clearFilterPreview(): void {
  if (!engine) return
  previewSeq++
  const r = livePreview
  livePreview = null
  engine.compositor.liveFilter = null
  if (r) {
    releaseFilterRun(r)
    requestFrame(true, r.docRect)
  }
}

/** フィルターを確定してレイヤーへ書き戻す (選択範囲があればその中だけ)。履歴 1 エントリ */
export async function applyFilter(layerId: string, filterId: string, params: FilterParams, label = 'history.filter'): Promise<boolean> {
  if (!engine) return false
  clearFilterPreview()
  const docStore = useDocumentStore()
  const layer = docStore.rasterLayer(layerId)
  if (!layer || layer.lock.all || layer.lock.pixels) return false
  const gen = docStore.doc?.pixelGeneration
  const pending = runFilterOn(layerId, filterId, params)
  const r = pending instanceof Promise ? await pending : pending
  if (!r) return false
  if (!engine || r.owner !== engine || !docStore.doc || docStore.doc.pixelGeneration !== gen || !docStore.rasterLayer(layerId)) {
    // 実行中に画素が変わった (別の編集が入った) → 捨てる
    releaseFilterRun(r)
    return false
  }
  const out = engine.filter.readPixels(r.fb, { x: 0, y: 0, w: r.local.w, h: r.local.h })
  releaseFilterRun(r)
  const mask = docStore.selectionMask()
  let rect = r.local
  let result = out
  if (mask) {
    // 選択範囲内だけ変える: 元画素と線形補間
    const alpha = maskToAlpha(mask, r.docRect)
    const orig = pixelStore.get(layer.pixelId)!.readRect(r.local)
    for (let i = 0; i < r.local.w * r.local.h; i++) {
      const m = alpha[i]! / 255
      if (m >= 1) continue
      for (let c = 0; c < 4; c++) out[i * 4 + c] = Math.round(orig[i * 4 + c]! * (1 - m) + out[i * 4 + c]! * m)
    }
    result = out
  }
  const docRect = r.docRect
  docStore.mutate({ key: label, params: { name: filterId } }, (draft, px) => {
    px.writeRect(layer.pixelId, rect, result)
    void draft
    return { kind: 'rect', rect: docRect }
  })
  return true
}
